/**
 * Markup Cost-Key Classification (Story 17.4 — Epic 17 Ad Spend Markup)
 *
 * The PURE, registry-driven answer to "is this metric key markup-affected for
 * platform P, and in which direction?" — extracted to `@provenleap/integrations`
 * so the server markup engine (`apps/server/src/services/markup-engine.ts`,
 * Story 17.3) AND the web report-editor markup indicator
 * (`DataSourceSection.tsx`, Story 17.4) consume the SAME derivation from the
 * SAME source list. No duplicated cost lists (Story 17.4 AC #9 / Trap #4 — the
 * widget-type-allowlist failure mode the repo already suffers; do not create a
 * fourth instance with cost-metric keys).
 *
 * The server engine re-exports `MARKUP_SPEND_BASE_KEYS`,
 * `classifyMarkupDirections`, and the `MarkupDirection` / `MarkupMetricDefinition`
 * types from here, so Story 17.3's pipeline math and its `markup-engine.test.ts`
 * census (the regression lock for the base keys) are unchanged.
 *
 * ## Classification rules (registry-driven — NEVER "all Currency metrics")
 *
 * Direction is derived from each platform's metric registry
 * (`getRegistryForPlatform(platform).metrics`, Story 5.35/31-31):
 * - key ∈ MARKUP_SPEND_BASE_KEYS (additive spend) → multiply by factor
 * - ratio whose `ratioComponents.numerator` ∈ base set → multiply
 *   (cpc, cpm, cost_per_conversion, average_cost, eCPC, …)
 * - ratio whose `ratioComponents.denominator` ∈ base set → divide
 *   (roas, purchase_roas, ROAS14d — spend up means return-on-spend down)
 * - everything else → untouched. `dataType: "Currency"` alone is NOT the
 *   predicate: REVENUE metrics (conversions_value, purchase_value, gmv,
 *   sales14d, …) MUST never be scaled — marking them up would inflate the
 *   client's reported revenue and silently re-inflate ROAS.
 */

// ============================================================================
// Types
// ============================================================================

export type MarkupDirection = "multiply" | "divide";

/** The registry-definition subset classification reads (31-31 fields). */
export interface MarkupMetricDefinition {
  readonly key: string;
  readonly aggregationKind?: "additive" | "ratio";
  readonly ratioComponents?: {
    readonly numerator?: string;
    readonly denominator: string;
  };
}

// ============================================================================
// Spend base keys (Story 17.3 Task 2.1 census, 2026-06-12)
// ============================================================================

/**
 * Additive ad-SPEND metric keys across every registered ad adapter — the
 * roots cost-classification reachability is computed from.
 *
 * Census method (re-run when adding an ad adapter — the
 * `markup-engine.test.ts` census test walks `getAllRegisteredMetrics()` and
 * fails on drift): every `dataType: "Currency"` + `aggregationKind:
 * "additive"` metric on an ads platform whose key names advertiser COST
 * (spend/cost/fee), explicitly EXCLUDING Currency-typed REVENUE metrics
 * (conversions_value, purchase_value, gmv, sales*, *Revenue, …) and
 * third-party cost ESTIMATES (ahrefs/semrush traffic-cost metrics are
 * competitor estimates, not the client's spend; shopify cost_of_goods_sold
 * is COGS; klaviyo text_message_spend is messaging spend, not ad spend).
 *
 * SYNC note: `SPEND_METRIC_KEYS` in `widget-data-transformer.ts` is a
 * DIFFERENT, narrower list (goal-widget "lower is better" heuristics) — do
 * not merge the two; they serve different purposes.
 */
export const MARKUP_SPEND_BASE_KEYS: ReadonlySet<string> = new Set([
  // google-ads + amazon-ads (Sponsored Products)
  "cost",
  // facebook-ads / instagram-ads / linkedin-ads / tiktok-business /
  // microsoft-ads / pinterest-ads / snapchat-ads / reddit-ads / amazon-ads (SB)
  "spend",
  // facebook-ads / instagram-ads (subset of spend on social surfaces)
  "social_spend",
  // linkedin-ads (USD-normalized sibling of spend)
  "spend_usd",
  // microsoft-ads (daily-grain spend column)
  "dailySpend",
  // pinterest-ads (micro-currency sibling of spend — scaled, never summed)
  "spendMicro",
  // amazon-ads (SP 1-year lookback spend)
  "spend1y",
  // amazon-ads DSP total + component costs/fees (uniform factor preserves
  // their additivity: totalCost = supplyCost + fees still holds post-markup)
  "totalCost",
  "adGroupCost",
  "matchedTargetCost",
  "targetingCost",
  "supplyCost",
  "mashupCost",
  "technicalCost",
  "totalFee",
  "agencyFee",
  "amazonAudienceFee",
  "amazonPlatformFee",
  "3PFees",
  // google-analytics-4 imported ad cost — included so a global markup keeps
  // GA4's view of ad spend consistent with the ads platforms' own numbers
  // (an unscaled GA4 cost next to a scaled google-ads cost would expose the
  // markup — the exact thing Epic 17 exists to prevent).
  "advertiserAdCost",
]);

// ============================================================================
// Classification
// ============================================================================

/**
 * Derive the per-metric markup direction map from a platform's registry
 * definitions. See the header doc for the three rules. Keys that classify in
 * BOTH directions (spend/spend ratio — none exist today) cancel and are
 * left untouched.
 */
export function classifyMarkupDirections(
  metricDefs: Iterable<MarkupMetricDefinition>,
): Map<string, MarkupDirection> {
  const directions = new Map<string, MarkupDirection>();
  for (const def of metricDefs) {
    if (def.aggregationKind === "ratio") {
      const numerator = def.ratioComponents?.numerator;
      const denominator = def.ratioComponents?.denominator;
      const numeratorIsSpend =
        numerator !== undefined && MARKUP_SPEND_BASE_KEYS.has(numerator);
      const denominatorIsSpend =
        denominator !== undefined && MARKUP_SPEND_BASE_KEYS.has(denominator);
      if (numeratorIsSpend && !denominatorIsSpend) {
        directions.set(def.key, "multiply");
      } else if (denominatorIsSpend && !numeratorIsSpend) {
        directions.set(def.key, "divide");
      }
      continue;
    }
    if (MARKUP_SPEND_BASE_KEYS.has(def.key)) {
      directions.set(def.key, "multiply");
    }
  }
  return directions;
}

// ============================================================================
// Web-facing convenience: per-(metricKey, platform) markup-affected lookup
// ============================================================================

/**
 * Returns the markup direction for a single (metricKey, platform) pair, or
 * `undefined` when the key is not markup-affected on that platform. Pure over
 * the registry definitions the caller supplies — decoupled from
 * `getRegistryForPlatform` so it stays unit-testable without adapter
 * registration side-effects (the web indicator passes the registry's metrics;
 * see `isMarkupAffectedMetricKey` for the registry-bound wrapper).
 */
export function markupDirectionForMetric(
  metricKey: string,
  metricDefs: Iterable<MarkupMetricDefinition>,
): MarkupDirection | undefined {
  return classifyMarkupDirections(metricDefs).get(metricKey);
}

/**
 * Whether ANY of the given metric keys is markup-affected on `platform` —
 * the predicate the report-editor markup indicator gates on (Story 17.4
 * AC #10b). Pure over the platform's registry metric definitions the caller
 * supplies (the web layer reads them once from `getRegistryForPlatform`); a
 * platform with no registered registry → no classified keys → false.
 */
export function anyMetricMarkupAffected(
  metricKeys: readonly string[],
  metricDefs: Iterable<MarkupMetricDefinition>,
): boolean {
  if (metricKeys.length === 0) return false;
  const directions = classifyMarkupDirections(metricDefs);
  return metricKeys.some((key) => directions.has(key));
}
